import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { InfoTipsComponent } from '../../dialogs/info-tips/info-tips.component';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ISelect, ISelectItem } from '../../store/models/select.model';
import { EContentGreeting, IContentFindResponse, IContentItem } from '../../store/models/content.model';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { Subject } from 'rxjs';
import { ECampaignGoal } from '../../store/models/campaigns.model';
import { ContentService } from '../../store/services/content.service';
import { Router } from '@angular/router';
import { SessionService } from '../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { IUserSessionData } from '../../store/models/user.model';
import { IContentGroup } from '../../store/models/content-group.model';

@Component({
  selector: 'app-add-content-code',
  templateUrl: './add-content-code.component.html',
  styleUrls: ['./add-content-code.component.scss'],
})
export class AddContentCodeComponent implements OnInit, OnDestroy {
  @ViewChild('attention') attention: ElementRef;
  @ViewChild('benefit_1') benefit1: ElementRef;
  @ViewChild('benefit_2') benefit2: ElementRef;
  @ViewChild('closing') closing: ElementRef;
  @ViewChild('details') details: ElementRef;
  @ViewChild('opening') opening: ElementRef;

  greetingsSelect: ISelect<any> = {
    label: 'Select greeting',
    items: [
      {
        label: EContentGreeting.HI_FIRST_NAME,
        value: EContentGreeting.HI_FIRST_NAME,
      },
      {
        label: EContentGreeting.FIRST_NAME,
        value: EContentGreeting.FIRST_NAME,
      },
      {
        label: EContentGreeting.ALOHA,
        value: EContentGreeting.ALOHA,
      },
      {
        label: EContentGreeting.NEW,
        value: 'new',
      },
    ],
  };
  backButton: Button = {
    color: ButtonColor.BLUE,
    route: ['/contents/content_group'],
    size: ButtonSize.SMALL,
    text: 'Back',
    style: 'outline',
  };
  previewButton: Button = {
    color: ButtonColor.ORANGE,
    size: ButtonSize.SMALL,
    text: 'Save',
    onClick: () => this.updateCampaign(),
    style: 'filled',
  };
  formGroup: FormGroup;
  componentReady = false;
  showCustomGreeting: boolean;
  backToGreetingsButton: Button = {
    text: 'Back to greetings',
    color: ButtonColor.WHITE,
    size: ButtonSize.SMALL,
    onClick: () => (this.showCustomGreeting = false),
  };
  variables = [];

  selection = null;
  validationError: boolean;

  contentItems: ISelectItem[] = [];

  selectContentProperties: ISelect<IContentItem> = {
    label: 'Select Existing Content',
  };

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private editMode: boolean;

  private userSession: IUserSessionData;

  constructor(
    private contentService: ContentService,
    private store: Store<IReducers>,
    private readonly router: Router,
    private readonly sessionsService: SessionService,
  ) {
  }

  get content_code() {
    return this.formGroup.get('content_code');
  }

  get first_benefit() {
    return this.formGroup.get('first_benefit');
  }

  get second_benefit() {
    return this.formGroup.get('second_benefit');
  }

  get content_attention() {
    return this.formGroup.get('content_attention');
  }

  get content_details() {
    return this.formGroup.get('content_details');
  }

  onGreetingsSelection = value => {
    if (value === 'new') {
      this.formGroup.get('custom_greeting').setValue('');
      this.showCustomGreeting = true;
    } else {
      this.formGroup.get('custom_greeting').setValue(value);
    }
  };

  updateCampaign() {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      return;
    }
    this.contentService.createContentGroup(this.content_code.value).subscribe(
      response => {
        if (response && response.content_id) {
          const data = this.formGroup.value;
          data.content_group_id = response.content_id;
          if (data.custom_greeting) {
            data.greeting = data.custom_greeting;
            delete data.custom_greeting;
          }
          this.contentService.createAdminContentCode(data).subscribe(
            contentResponse => {
              this.store.dispatch(
                openSnackbar({
                  snackbarData: {
                    data: {
                      message: 'Content code created successfully',
                      type: 'success',
                    },
                  },
                }),
              );
              this.router.navigate([
                '/contents',
                'preview-master-content',
                data.content_group_id,
              ]);
            },
            error =>
              this.store.dispatch(
                openSnackbar({
                  snackbarData: {
                    data: {
                      type: 'error',
                      message: error.error,
                    },
                  },
                }),
              ),
          );
        }
      },
      error =>
        this.store.dispatch(
          openSnackbar({
            snackbarData: {
              data: {
                type: 'error',
                message: error.error,
              },
            },
          }),
        ),
    );
  }

  ngOnInit() {
    this.sessionsService.onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.userSession = session;
        this.contentService
          .getAllContentGroups(this.userSession.id)
          .subscribe((contentGroups: IContentGroup[]) => {
            if (contentGroups && contentGroups.length > 0) {
              const newContentItems = [];
              for (const contentGroup of contentGroups) {
                newContentItems.push({
                  label: contentGroup.content_group,
                  value: contentGroup.content_group,
                });
              }

              if (newContentItems.length) {
                this.contentItems = [...this.contentItems, ...newContentItems];
              }
            }
          });
        this.formGroup = new FormGroup({
          content_code: new FormControl('', [Validators.required]),
          campaign_goal: new FormControl(ECampaignGoal.PHONE),
          greeting: new FormControl(EContentGreeting.HI_FIRST_NAME),
          custom_greeting: new FormControl(),
          quick_answer: new FormControl('', [Validators.required]),
          content_attention: new FormControl('', [Validators.required]),
          first_benefit: new FormControl('', [Validators.required]),
          second_benefit: new FormControl('', [Validators.required]),
          content_details: new FormControl('', [Validators.required]),
        });
        this.componentReady = true;
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
    this.destroy$.complete();
  }

  openExamplesDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: 'example_attention',
          },
        },
      }),
    );
  }

  onContentSelect(contentId: string) {
    if (contentId) {
      this.contentService
        .findContentByCode(contentId)
        .subscribe((contents: IContentFindResponse) => {
          this.fillContents(contents);
        });
    }
  }

  fillContents(content: IContentFindResponse) {
    if (content.campaign_goal) {
      this.formGroup.get('campaign_goal').setValue(content.campaign_goal);
    }

    if (content.quick_answer) {
      this.formGroup.get('quick_answer').setValue(content.quick_answer);
    }

    if (content.content_attention) {
      this.formGroup.get('content_attention').setValue(content.content_attention);
    }

    if (content.first_benefit) {
      this.formGroup.get('first_benefit').setValue(content.first_benefit);
    }

    if (content.second_benefit) {
      this.formGroup.get('second_benefit').setValue(content.second_benefit);
    }

    if (content.content_details) {
      this.formGroup.get('content_details').setValue(content.content_details);
    }

    if (content.greeting) {
      if (
        [
          EContentGreeting.FIRST_NAME.toString(),
          EContentGreeting.HI_FIRST_NAME.toString(),
          EContentGreeting.ALOHA.toString(),
        ].indexOf(content.greeting) === -1
      ) {
        this.showCustomGreeting = true;
        this.formGroup.get('custom_greeting').setValue(content.greeting);
      } else {
        this.formGroup.get('greeting').setValue(content.greeting);
      }
    }
  }
}
